import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import { getSelectedProductAttributeValuesTree } from '@/models/productAttribute';
import { ProductAttributeValueWithParent } from '@/models/request';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';

const INDENTATION_PX = 8;

type ProductAttributeValueHierarchyNode = ReturnType<
  typeof getSelectedProductAttributeValuesTree<ProductAttributeValueWithParent>
>[number];

const ProductAttributeValueHierarchyBranch = ({
  node,
  depth,
}: {
  node: ProductAttributeValueHierarchyNode;
  depth: number;
}) => {
  const { _db } = useDatabaseTranslation();
  const attributeName = node.value.productAttribute.name as I18nWording;
  const valueName = node.value.name as I18nWording;

  return (
    <div style={{ paddingLeft: `${depth * INDENTATION_PX}px` }}>
      <InlineText size="200" weight="medium">
        {_db(attributeName)}:
      </InlineText>{' '}
      <InlineText size="200" color="secondary">
        {_db(valueName) || '-'}
      </InlineText>
      {node.children.map((childNode) => (
        <ProductAttributeValueHierarchyBranch
          key={childNode.value.id}
          node={childNode}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

export const ProductAttributeValuesHierarchy = ({
  productAttributeValues,
}: {
  productAttributeValues: ProductAttributeValueWithParent[];
}) => {
  const { i18n, t } = useLingui();
  const hierarchy = getSelectedProductAttributeValuesTree({ productAttributeValues });
  const selectedValuesCount = productAttributeValues.length;
  const selectedValuesLabel = i18n._({
    id: 'settings.shared.product-attribute-values.selected-count',
    message: '{count} values selected',
    values: { count: selectedValuesCount },
  });

  if (!selectedValuesCount) {
    return <InlineText color="secondary">-</InlineText>;
  }

  return (
    <Stack gap="0.25rem" alignItems="start">
      <InlineText size="200" color="secondary">
        {selectedValuesLabel}
      </InlineText>
      <Drawer
        trigger={
          <Button variant="link-subtle" size="small">
            <Trans id="settings.shared.product-attribute-values.see-details">See details</Trans>
          </Button>
        }
        style={{ maxWidth: '31.25rem' }}
      >
        <DrawerHeader>
          <Title size="300">
            {t({
              id: 'settings.shared.product-attribute-values.drawer.title',
              message: 'Selected product attribute values',
            })}
          </Title>
        </DrawerHeader>
        <DrawerBody>
          <Stack gap="0.5rem" padding="1rem 1.5rem">
            {hierarchy.map((node) => (
              <ProductAttributeValueHierarchyBranch key={node.value.id} node={node} depth={0} />
            ))}
          </Stack>
        </DrawerBody>
      </Drawer>
    </Stack>
  );
};
